// ---------------------------------------------------------------
// taskService.js — Tareas de turno de la farmacia.
//
// Colección `tasks` (las crea el ADMIN): definición de cada tarea
//   { title, desc, assignedTo: uid | "all", repeat: "once" |
//     "daily" | "weekly", date: "YYYY-MM-DD" (día, o inicio si se
//     repite), weekday: 0-6 (getDay, sólo weekly), createdAt }
//
// Colección `taskDone` (la escribe cada empleado): una marca de
// completado POR TAREA Y DÍA, con id `${taskId}_${fecha}`:
//   { taskId, date, doneBy, doneByName, doneAt }
// Así las tareas repetidas (caducidades, SIGRE…) aparecen cada día
// sin duplicar definiciones, y el admin ve en tiempo real quién
// completó qué.
// ---------------------------------------------------------------
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { sharedSubscribe } from "../utils/sharedSnapshot";

/** Plantillas rápidas de tareas habituales de farmacia */
export const TASK_TEMPLATES = [
  {
    title: "Revisión de caducidades",
    desc: "Repasar las caducidades próximas y retirar lo que venza este mes.",
  },
  {
    title: "Recepción pedido Cofares",
    desc: "Recibir, comprobar y dar de alta el pedido del almacén (Cofares).",
  },
  {
    title: "Recepción pedido HEFAME",
    desc: "Recibir, comprobar y dar de alta el pedido del almacén (HEFAME).",
  },
  {
    title: "Reposición de líneas",
    desc: "Reponer estanterías y lineales con el género recibido.",
  },
  {
    title: "Vaciar contenedor SIGRE",
    desc: "Vaciar y gestionar el punto SIGRE de recogida de medicamentos.",
  },
];

const pad2 = (n) => String(n).padStart(2, "0");

/** Repeticiones "por periodos" (mensual, semestral, anual): cada
 *  ocurrencia cae en un mes distinto, así que admiten el cambio
 *  puntual de día por mes (task.overrides["YYYY-MM"]). */
export const isPeriodicRepeat = (task) =>
  ["monthly", "monthlyFirstWork", "semiannual", "yearly"].includes(task.repeat);

/** Meses entre ocurrencias según el tipo de repetición */
const periodStep = (repeat) =>
  repeat === "yearly" ? 12 : repeat === "semiannual" ? 6 : 1;

/**
 * Primer día "trabajable" de un mes ("YYYY-MM"):
 *  · Con el horario de la persona asignada: su primer día CON turno
 *    (ni baja ni descanso ni vacaciones).
 *  · Sin horario (tarea de equipo, o mes aún sin publicar): el
 *    primer día que no sea domingo (la farmacia abre L-S).
 */
export function firstWorkableDayISO(monthISO, scheduleDays) {
  const [y, m] = monthISO.split("-").map(Number);
  const total = new Date(y, m, 0).getDate();
  for (let d = 1; d <= total; d++) {
    const iso = `${monthISO}-${pad2(d)}`;
    if (scheduleDays) {
      const info = scheduleDays[String(d)];
      if (info?.shifts?.length && !info.baja) return iso;
    } else if (new Date(`${iso}T12:00:00`).getDay() !== 0) {
      return iso;
    }
  }
  return null;
}

/**
 * ¿La tarea toca el día indicado? `scheduleDays` es el horario del
 * mes de la persona asignada (sólo se usa en las mensuales de
 * "primer día laboral"). Un cambio puntual del admin
 * (task.overrides["YYYY-MM"] = "YYYY-MM-DD") manda sobre la serie.
 */
/** ¿Es domingo? (la farmacia no abre) */
const esDomingo = (dateISO) =>
  new Date(`${dateISO}T12:00:00`).getDay() === 0;

export function taskOccursOn(task, dateISO, scheduleDays = null) {
  // Excepciones: días de los que se ha desligado una ocurrencia
  // (p. ej. al editar "sólo esta tarea" de una serie).
  if (task.exceptions?.includes(dateISO)) return false;
  if (task.repeat === "once" || !task.repeat) return task.date === dateISO;
  if (isPeriodicRepeat(task)) {
    const monthISO = dateISO.slice(0, 7);
    if (monthISO < task.date.slice(0, 7)) return false; // aún no empieza
    const override = task.overrides?.[monthISO];
    if (override) return override === dateISO;

    // ¿Le toca a ESTE mes? (1 = mensual, 6 = semestral, 12 = anual)
    const [sy, sm] = task.date.slice(0, 7).split("-").map(Number);
    const [y, m] = monthISO.split("-").map(Number);
    const elapsed = (y - sy) * 12 + (m - sm);
    if (elapsed < 0 || elapsed % periodStep(task.repeat) !== 0) return false;

    let occurs;
    if (task.repeat === "monthlyFirstWork") {
      occurs = firstWorkableDayISO(monthISO, scheduleDays);
    } else {
      const total = new Date(y, m, 0).getDate();
      // Día del mes de la fecha de inicio (día 31 → último del mes corto)
      const day = Math.min(Number(task.date.slice(8, 10)), total);
      occurs = `${monthISO}-${pad2(day)}`;
    }
    return occurs === dateISO && dateISO >= task.date;
  }
  if (dateISO < task.date) return false; // aún no ha empezado
  if (task.repeat === "daily") {
    // Los domingos la farmacia no abre, así que una tarea "a diario"
    // va de lunes a sábado. Antes salían también en domingo y se
    // quedaban ahí sin que nadie pudiera hacerlas.
    return !esDomingo(dateISO);
  }
  if (task.repeat === "weekly") {
    return new Date(`${dateISO}T12:00:00`).getDay() === task.weekday;
  }
  return false;
}

/* ---- Asignación: "all" | uid | [uid, uid, …] (compatible) ---- */
export const isTeamTask = (task) => task.assignedTo === "all";

/** ¿La tarea está asignada a este usuario (o al equipo)? */
export function isAssignedTo(task, uid) {
  const a = task.assignedTo;
  if (a === "all") return true;
  if (Array.isArray(a)) return a.includes(uid);
  return a === uid;
}

/** Lista de uids asignados (vacía si es del equipo) */
export function assigneeUids(task) {
  const a = task.assignedTo;
  if (a === "all" || !a) return [];
  return Array.isArray(a) ? a : [a];
}

/** Etiqueta legible de la repetición */
export function repeatLabel(task) {
  if (task.repeat === "daily") return "Cada día";
  if (task.repeat === "weekly") {
    const names = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
    return `Cada ${names[task.weekday] || ""}`;
  }
  if (task.repeat === "monthly") return `Cada mes (día ${Number(task.date.slice(8, 10))})`;
  if (task.repeat === "monthlyFirstWork") return "Primer día laboral del mes";
  if (task.repeat === "semiannual" || task.repeat === "yearly") {
    const MESES = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
    ];
    const day = Number(task.date.slice(8, 10));
    const mes = MESES[Number(task.date.slice(5, 7)) - 1];
    return task.repeat === "yearly"
      ? `Cada año (${day} de ${mes})`
      : `Cada 6 meses (desde el ${day} de ${mes})`;
  }
  return null;
}

/** Tareas que TOCAN HOY a un usuario (las suyas y las del equipo);
 *  para la campana de notificaciones. */
/**
 * Tareas que tocan HOY según quién mira:
 *  · Admin: todas las del día (las de "primer día laboral" asignadas
 *    a una sola persona se resuelven con el horario de esa persona).
 *  · Empleado: las suyas y las del equipo.
 */
export function todaysTasks(
  tasks,
  { isAdmin, uid, dateISO, scheduleDays, schedulesByUser }
) {
  if (!isAdmin) return tasksDueFor(tasks, uid, dateISO, scheduleDays);
  return tasks.filter((t) => {
    const uids = assigneeUids(t);
    const sched =
      t.repeat === "monthlyFirstWork" && uids.length === 1
        ? schedulesByUser?.[uids[0]]?.days || null
        : null;
    return taskOccursOn(t, dateISO, sched);
  });
}

export function tasksDueFor(tasks, uid, dateISO, myScheduleDays) {
  return tasks.filter((t) => {
    if (!isAssignedTo(t, uid)) return false;
    // Sólo se usa el horario propio cuando la tarea NO es del equipo
    const sched = !isTeamTask(t) ? myScheduleDays : null;
    return taskOccursOn(t, dateISO, sched);
  });
}

/* ---------------- Definiciones (admin) ---------------- */
/** Todas las tareas, con UNA sola conexión para toda la web */
/**
 * Tareas visibles para quien mira.
 *  · Admin: todas.
 *  · Empleado: SÓLO las suyas y las del equipo. No es un filtro de
 *    pantalla: son tres consultas acotadas, así que las tareas de
 *    otros ni siquiera se descargan (y las reglas de Firestore lo
 *    impiden aunque se intente saltar la interfaz).
 * Los listeners van por `sharedSubscribe`, así que si varias
 * pantallas piden lo mismo se comparte la misma escucha.
 */
export function subscribeTasks(onData, onError, { uid, isAdmin } = {}) {
  if (isAdmin || !uid) {
    return sharedSubscribe(
      "tasks",
      (cb, err) =>
        onSnapshot(
          collection(db, "tasks"),
          (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          err
        ),
      onData,
      onError
    );
  }

  // Empleado: equipo + asignadas a él (campo suelto o dentro de lista)
  const partes = [
    ["all", query(collection(db, "tasks"), where("assignedTo", "==", "all"))],
    ["mine", query(collection(db, "tasks"), where("assignedTo", "==", uid))],
    [
      "list",
      query(collection(db, "tasks"), where("assignedTo", "array-contains", uid)),
    ],
  ];
  const buffer = { all: [], mine: [], list: [] };
  const emitir = () => {
    const vistos = new Set();
    const salida = [];
    for (const k of ["all", "mine", "list"]) {
      for (const t of buffer[k]) {
        if (vistos.has(t.id)) continue;
        vistos.add(t.id);
        salida.push(t);
      }
    }
    onData(salida);
  };
  const offs = partes.map(([clave, q]) =>
    sharedSubscribe(
      `tasks:${clave}:${uid}`,
      (cb, err) =>
        onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), err),
      (lista) => {
        buffer[clave] = lista;
        emitir();
      },
      onError
    )
  );
  return () => offs.forEach((off) => off && off());
}

export function createTask({ title, desc, assignedTo, repeat, date }) {
  return addDoc(collection(db, "tasks"), {
    title,
    desc: desc || "",
    assignedTo: assignedTo || "all",
    repeat: repeat || "once",
    date,
    weekday: repeat === "weekly" ? new Date(`${date}T12:00:00`).getDay() : null,
    createdAt: serverTimestamp(),
  });
}

/** Actualiza los campos de una tarea (edita TODA la serie) */
export function updateTask(taskId, { title, desc, assignedTo, repeat, date }) {
  return setDoc(
    doc(db, "tasks", taskId),
    {
      title,
      desc: desc || "",
      assignedTo: assignedTo || "all",
      repeat: repeat || "once",
      date,
      weekday:
        repeat === "weekly" ? new Date(`${date}T12:00:00`).getDay() : null,
    },
    { merge: true }
  );
}

/**
 * Edita SÓLO la ocurrencia de un día: desliga ese día de la serie
 * (excepción) y crea una tarea suelta con los cambios para ese día.
 */
export async function editSingleOccurrence(task, dateISO, patch) {
  await setDoc(
    doc(db, "tasks", task.id),
    { exceptions: arrayUnion(dateISO) },
    { merge: true }
  );
  await createTask({
    title: patch.title,
    desc: patch.desc,
    assignedTo: patch.assignedTo,
    repeat: "once",
    date: dateISO,
  });
}

/** Borra la tarea (si se repite, desaparece la serie completa) */
export function deleteTask(taskId) {
  return deleteDoc(doc(db, "tasks", taskId));
}

/** El admin mueve la tarea SOLO ese mes a otro día (o la
 *  restablece pasando dateISO = null), sin tocar la serie. */
export function setTaskOverride(taskId, monthISO, dateISO) {
  return setDoc(
    doc(db, "tasks", taskId),
    { overrides: { [monthISO]: dateISO || deleteField() } },
    { merge: true }
  );
}

/* ---------------- Completados (empleados) ---------------- */
export function subscribeTaskDone(dateISO, onData, onError) {
  return sharedSubscribe(
    `taskDone:${dateISO}`,
    (cb, err) =>
      onSnapshot(
        query(collection(db, "taskDone"), where("date", "==", dateISO)),
        (snap) => {
          const map = {};
          snap.docs.forEach((d) => (map[d.data().taskId] = d.data()));
          cb(map);
        },
        err
      ),
    onData,
    onError
  );
}

/** Las tareas marcadas entre dos fechas (ambas incluidas), como un Set
 *  de `${taskId}_${fecha}`: para el resumen de lo que queda sin marcar */
export function subscribeTaskDoneRango(desdeISO, hastaISO, onData, onError) {
  return onSnapshot(
    query(
      collection(db, "taskDone"),
      where("date", ">=", desdeISO),
      where("date", "<=", hastaISO)
    ),
    (snap) => onData(new Set(snap.docs.map((d) => `${d.data().taskId}_${d.data().date}`))),
    onError
  );
}

/** Marca la tarea como completada hoy (queda registrado quién) */
export function markTaskDone(taskId, dateISO, uid, name) {
  return setDoc(doc(db, "taskDone", `${taskId}_${dateISO}`), {
    taskId,
    date: dateISO,
    doneBy: uid,
    doneByName: name || "",
    doneAt: serverTimestamp(),
  });
}

/** Desmarca (quien la marcó, o el admin) */
export function unmarkTaskDone(taskId, dateISO) {
  return deleteDoc(doc(db, "taskDone", `${taskId}_${dateISO}`));
}
